import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
  alpha,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Security,
  Description,
  Timer,
  Shield,
  VerifiedUser,
  GppBad,
  Warning,
  Error as ErrorIcon,
  CheckCircle,
  Delete,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import api from '../shared/services/api';
import { brand, primaryButtonSx } from '../theme';
import { stepperIcons } from '../assets/icons/nav';
import NavIcon from '../shared/components/NavIcon';

interface SecurityScanResult {
  id: string;
  filename: string;
  file_id: string;
  is_safe: boolean;
  security_level: string;
  risk_score: number;
  max_prompt_injection_score: number;
  detected_threats: any;
  flagged_chunks: any[];
  blocked_reason: string | null;
  processing_time_ms: number;
  created_at: string;
}

interface FileUpload {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  result?: SecurityScanResult;
  error?: string;
}

const PDFSecurityScanner: React.FC = () => {
  const theme = useTheme();
  const [files, setFiles] = useState<FileUpload[]>([]);
  const [uploading, setUploading] = useState(false);

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const scanFile = async (fileUpload: FileUpload) => {
    try {
      const base64Content = await fileToBase64(fileUpload.file);

      const uploadData = {
        filename: fileUpload.file.name,
        content: base64Content,
        content_type: fileUpload.file.type
      };

      const response = await api.post(
        '/api/v1/pdf-security/scan',
        uploadData,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      return { success: true, data: response.data };
    } catch (err: any) {
      return {
        success: false,
        error: err.response?.data?.detail || err.message || 'Scan failed'
      };
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const pdfFiles = acceptedFiles.filter(file => 
      file.name.toLowerCase().endsWith('.pdf')
    );

    if (pdfFiles.length === 0) {
      return;
    }

    const newFiles: FileUpload[] = pdfFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      status: 'pending' as const,
    }));

    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const scanAllFiles = async () => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    
    if (pendingFiles.length === 0) {
      return;
    }

    setUploading(true);

    // Process files sequentially
    for (const fileUpload of pendingFiles) {
      setFiles(prev => prev.map(f => 
        f.id === fileUpload.id ? { ...f, status: 'uploading' as const } : f
      ));

      const result = await scanFile(fileUpload);

      if (result.success) {
        setFiles(prev => prev.map(f => 
          f.id === fileUpload.id 
            ? { ...f, status: 'completed' as const, result: result.data } 
            : f
        ));
      } else {
        setFiles(prev => prev.map(f => 
          f.id === fileUpload.id 
            ? { ...f, status: 'error' as const, error: result.error } 
            : f
        ));
      }
    }

    setUploading(false);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
  });

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const getSecurityLevelColor = (level: string) => {
    switch (level?.toLowerCase()) {
      case 'blocked': return theme.palette.error.main;
      case 'high_risk': return theme.palette.error.main;
      case 'medium_risk': return theme.palette.warning.main;
      case 'low_risk': return theme.palette.info.main;
      case 'safe': return theme.palette.success.main;
      default: return theme.palette.grey[500];
    }
  };

  const getSecurityLevelIcon = (level: string) => {
    switch (level?.toLowerCase()) {
      case 'blocked': return <GppBad />;
      case 'high_risk': return <Warning />;
      case 'medium_risk': return <Warning />;
      case 'low_risk': return <Shield />;
      case 'safe': return <VerifiedUser />;
      default: return <Security />;
    }
  };

  return (
    <Box sx={{ height: '100%', bgcolor: 'grey.50', overflow: 'auto', py: 4 }}>
        <Container maxWidth="lg">
          {/* Header Section */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Shield sx={{ fontSize: 40, color: brand.orange }} />
            <Box>
              <Typography variant="h4" fontWeight={600} sx={{ color: brand.textPrimary }}>
                PDF Security Scanner
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Detect prompt injection and security threats in PDF documents
              </Typography>
            </Box>
          </Stack>

          {/* Upload Section */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              mb: 3,
            }}
          >
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Upload PDF Files
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload one or multiple PDF files to scan for security threats
            </Typography>

            <Paper
              {...getRootProps()}
              elevation={0}
              sx={{
                p: 6,
                textAlign: 'center',
                cursor: 'pointer',
                border: '2px dashed',
                borderColor: isDragActive ? brand.orange : brand.borderSubtle,
                backgroundColor: brand.dropZoneBg,
                transition: 'all 0.3s',
                borderRadius: 1,
                '&:hover': { borderColor: brand.orange },
              }}
            >
              <input {...getInputProps()} />
              <NavIcon src={stepperIcons.dragDrop} size={48} sx={{ mx: 'auto', mb: 1 }} />
              <Typography variant="body1" gutterBottom sx={{ color: brand.textPrimary, fontWeight: 500 }}>
                {isDragActive ? 'Drop files here' : 'Drag & drop PDF files here'}
              </Typography>
              <Typography variant="caption" sx={{ color: brand.textSecondary, display: 'block' }}>
                or click to browse (supports multiple files)
              </Typography>
            </Paper>

            {uploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress sx={{ borderRadius: 1 }} />
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
                  Scanning files...
                </Typography>
              </Box>
            )}

            {files.length > 0 && (
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={scanAllFiles}
                  disabled={uploading || files.filter(f => f.status === 'pending').length === 0}
                  startIcon={<Security />}
                  sx={{ ...primaryButtonSx, flex: 1 }}
                >
                  Scan {files.filter(f => f.status === 'pending').length} File(s)
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setFiles([])}
                  disabled={uploading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 1,
                    borderColor: brand.borderSubtle,
                    color: brand.textPrimary,
                    '&:hover': { borderColor: brand.orange, backgroundColor: 'transparent' },
                  }}
                >
                  Clear All
                </Button>
              </Box>
            )}
          </Paper>

          {/* Results Section */}
          {files.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
                Scan Results ({files.length})
              </Typography>
              <Grid container spacing={2}>
                {files.map((fileUpload) => (
                  <Grid item xs={12} key={fileUpload.id}>
                    <Card
                      elevation={0}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                      }}
                    >
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          <Description sx={{ fontSize: 40, color: brand.orange, flexShrink: 0 }} />
                          
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight={600} noWrap>
                              {fileUpload.file.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(fileUpload.file.size / 1024).toFixed(2)} KB
                            </Typography>

                            {fileUpload.status === 'pending' && (
                              <Box sx={{ mt: 1 }}>
                                <Chip
                                  label="Ready to scan"
                                  size="small"
                                  color="default"
                                  variant="outlined"
                                />
                              </Box>
                            )}

                            {fileUpload.status === 'uploading' && (
                              <Box sx={{ mt: 2 }}>
                                <LinearProgress />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                  Scanning...
                                </Typography>
                              </Box>
                            )}

                            {fileUpload.status === 'completed' && fileUpload.result && (
                              <Box sx={{ mt: 2 }}>
                                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                                  <Chip
                                    icon={getSecurityLevelIcon(fileUpload.result.security_level)}
                                    label={fileUpload.result.is_safe ? 'SAFE' : 'BLOCKED'}
                                    sx={{
                                      bgcolor: alpha(getSecurityLevelColor(fileUpload.result.security_level), 0.1),
                                      color: getSecurityLevelColor(fileUpload.result.security_level),
                                      fontWeight: 600,
                                    }}
                                  />
                                  <Chip
                                    label={`${fileUpload.result.processing_time_ms}ms`}
                                    size="small"
                                    variant="outlined"
                                    icon={<Timer />}
                                  />
                                </Stack>

                                {fileUpload.result.blocked_reason && (
                                  <Alert severity="error" sx={{ mt: 2 }}>
                                    <Typography variant="body2" fontWeight={600}>
                                      Problem: {fileUpload.result.blocked_reason}
                                    </Typography>
                                    {fileUpload.result.detected_threats && (
                                      <Typography variant="body2" sx={{ mt: 1 }}>
                                        {fileUpload.result.detected_threats.type === 'banned_substring' 
                                          ? `Detected: "${fileUpload.result.detected_threats.detected}"`
                                          : `Type: ${fileUpload.result.detected_threats.type}`
                                        }
                                      </Typography>
                                    )}
                                  </Alert>
                                )}

                                {fileUpload.result.is_safe && (
                                  <Alert severity="success" sx={{ mt: 2 }}>
                                    <Typography variant="body2" fontWeight={600}>
                                      No security issues detected
                                    </Typography>
                                  </Alert>
                                )}
                              </Box>
                            )}

                            {fileUpload.status === 'error' && (
                              <Alert severity="error" sx={{ mt: 2 }}>
                                {fileUpload.error}
                              </Alert>
                            )}
                          </Box>

                          <Tooltip title="Remove">
                            <IconButton
                              size="small"
                              onClick={() => removeFile(fileUpload.id)}
                              sx={{ flexShrink: 0 }}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Container>
    </Box>
  );
};

export default PDFSecurityScanner;
